import { useMemo, useRef, useState } from "react";

export default function HomePage({ projects, onNewProject, onOpenProject, onEditProject, user, onSignOut }) {

  const [modal, setModal] = useState(null); // null | { mode: "new" | "edit", projectId? }
  const [form, setForm] = useState({ title: "", author: "", numBooks: 1, numChapters: 10, bookTitles: [""], chronicleName: "", coverUrl: "" });
  const coverInputRef = useRef(null);

  function openNew() {
    setForm({ title: "", author: "", numBooks: 1, numChapters: 10, bookTitles: [""], chronicleName: "", coverUrl: "" });
    setModal({ mode: "new" });
  }

  function openEdit(e, project) {
    e.stopPropagation();
    const cfg = project.data?.config || {};
    const numBooks = cfg.numBooks || 1;
    const bookTitles = Array.from({ length: numBooks }, (_, i) => cfg.bookTitles?.[i] || "");
    setForm({
      title: project.title || cfg.title || "",
      author: cfg.author || "",
      numBooks,
      numChapters: cfg.numChapters || 10,
      bookTitles,
      chronicleName: cfg.chronicleName || "",
      coverUrl: cfg.coverUrl || "",
    });
    setModal({ mode: "edit", projectId: project.id });
  }

  function setNumBooks(n) {
    const nb = Math.max(1, Math.min(10, Number(n)));
    setForm(f => ({
      ...f,
      numBooks: nb,
      bookTitles: Array.from({ length: nb }, (_, i) => f.bookTitles[i] || ""),
    }));
  }

  function setBookTitle(i, val) {
    setForm(f => {
      const bt = [...f.bookTitles];
      bt[i] = val;
      return { ...f, bookTitles: bt };
    });
  }

  function handleSubmit() {
    if (!form.title.trim()) return;
    if (modal.mode === "new") {
      onNewProject(form);
    } else {
      onEditProject(modal.projectId, form);
    }
    setModal(null);
  }

  function handlePickCover() {
    coverInputRef.current?.click();
  }

  function handleCoverFileChange(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      window.alert("Selecione um arquivo de imagem.");
      return;
    }
    // Keep local/session storage stable: very large base64 covers can exceed browser limits.
    if (file.size > 3 * 1024 * 1024) {
      window.alert("A imagem é muito grande. Escolha uma capa de até 3MB.");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = String(reader.result || "");
      setForm(f => ({ ...f, coverUrl: dataUrl }));
    };
    reader.onerror = () => window.alert("Não foi possível ler esta imagem.");
    reader.readAsDataURL(file);
  }

  function getProgress(project) {
    const data = project.data || {};
    const panel = data.bibliaPanel || {};
    const filledFields = Object.values(panel).filter(v => v && String(v).trim()).length;
    const bibliaProgress = Math.round((filledFields / 40) * 100);
    const chapters = data.chapters || [];
    const numChapters = data.config?.numChapters || 0;
    const doneChapters = chapters.filter(c => c.status === "done").length;
    const phase = data.phase || "biblia";
    const numBooks = data.config?.numBooks || 1;
    return { bibliaProgress, chapters: doneChapters, numChapters, phase, numBooks };
  }

  function getPhaseLabel(phase) {
    const map = { biblia:"Bíblia", characters:"Bíblia", refinement:"Bíblia", review:"Bíblia", config:"Configuração", outline:"Roteiro", scenes:"Roteiro", writing:"Escrita", done:"Concluído" };
    return map[phase] || "Bíblia";
  }

  function getPhaseColor(phase) {
    const map = {
      biblia:{ bg:"#fff8e0", color:"#7a5000" }, characters:{ bg:"#fff8e0", color:"#7a5000" },
      refinement:{ bg:"#fff8e0", color:"#7a5000" }, review:{ bg:"#fff8e0", color:"#7a5000" },
      config:{ bg:"#fde8cc", color:"#7a3a00" },
      outline:{ bg:"#e8f0ff", color:"#1a3a8b" }, scenes:{ bg:"#e8f0ff", color:"#1a3a8b" },
      writing:{ bg:"#e8f5e9", color:"#1b5e20" }, done:{ bg:"#f3e8ff", color:"#4a0072" },
    };
    return map[phase] || { bg:"#fff8e0", color:"#7a5000" };
  }

  function shortGenreLabel(genre) {
    const g = String(genre || "").trim();
    if (!g) return "";
    // Keep only the base genre before parenthetical details.
    const base = g.split("(")[0].trim();
    if (base.length <= 24) return base;
    return `${base.slice(0, 24).trim()}...`;
  }

  function timeAgo(dateStr) {
    if (!dateStr) return "";
    const diff = Date.now() - new Date(dateStr).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return "agora";
    if (mins < 60) return `${mins}min atrás`;
    const hours = Math.floor(mins / 60);
    if (hours < 24) return `${hours}h atrás`;
    const days = Math.floor(hours / 24);
    if (days < 30) return `${days}d atrás`;
    return `${Math.floor(days / 30)}m atrás`;
  }

  const sorted = useMemo(() => [...projects].sort((a, b) => new Date(b.updatedAt) - new Date(a.updatedAt)), [projects]);
  const totalWords = useMemo(() => {
    return sorted.reduce((sum, p) => {
      const text = (p.data?.chapters || [])
        .filter(c => c.status === "done" && c.content)
        .map(c => c.content)
        .join(" ");
      const count = text ? text.trim().split(/\s+/).length : 0;
      return sum + count;
    }, 0);
  }, [sorted]);

  return (
    <div style={{ fontFamily:"'Georgia',serif", minHeight:"100vh", background:"#131313", color:"#e5e2e1" }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@400;700;900&family=Crimson+Pro:ital,wght@0,400;0,600;1,400&display=swap');
        @import url('https://fonts.googleapis.com/css2?family=Material+Symbols+Outlined:wght,FILL@100..700,0..1&display=swap');
        *{box-sizing:border-box;margin:0;padding:0;}
        body{background:#131313;}
        .material-symbols-outlined {
          font-variation-settings: 'FILL' 0, 'wght' 400, 'GRAD' 0, 'opsz' 24;
          font-family: 'Material Symbols Outlined';
        }
        .hp-topbar{position:sticky;top:0;z-index:110;background:#131313;border-bottom:1px solid rgba(53,53,53,.3);}
        .hp-shell{display:flex;min-height:calc(100vh - 80px);}
        .hp-sidebar{width:260px;flex-shrink:0;background:#0e0e0e;padding:24px 14px;border-right:1px solid rgba(53,53,53,.25);}
        .hp-main{flex:1;padding:46px 42px 70px;background:#131313;}
        .hp-card{background:#353535;border:1px solid rgba(78,70,57,.4);border-radius:12px;overflow:hidden;box-shadow:0 18px 36px rgba(0,0,0,.35);transition:transform .2s,box-shadow .2s;border-color .2s;cursor:pointer;display:flex;flex-direction:column;}
        .hp-card:hover{transform:translateY(-4px);box-shadow:0 26px 46px rgba(0,0,0,.45);border-color:rgba(197,160,89,.45);}
        .hp-card-new{background:#1c1b1b;border:2px dashed #4e4639;border-radius:12px;min-height:360px;padding:30px;display:flex;flex-direction:column;align-items:center;justify-content:center;gap:10px;cursor:pointer;transition:border-color .2s,background .2s;}
        .hp-card-new:hover{border-color:#c5a059;background:#222120;}
        .hp-grid{display:grid;grid-template-columns:repeat(auto-fill,minmax(250px,1fr));gap:20px;}
        @media(max-width:600px){.hp-grid{grid-template-columns:1fr;}}
        .hp-continue-btn{width:100%;background:#c5a059;color:#261900;border:none;border-radius:8px;padding:13px 14px;font-family:'Crimson Pro',Georgia,serif;font-size:.97rem;font-weight:700;cursor:pointer;transition:all .18s;display:flex;align-items:center;justify-content:center;gap:8px;}
        .hp-continue-btn:hover{background:#e9c176;}
        .hp-edit-btn{background:rgba(14,14,14,.6);border:1px solid rgba(197,160,89,.3);color:#e9c176;border-radius:999px;padding:6px 10px;font-size:.7rem;cursor:pointer;transition:all .15s;font-family:'Crimson Pro',Georgia,serif;}
        .hp-edit-btn:hover{background:rgba(197,160,89,.12);border-color:#e9c176;}
        .hp-pw{background:#131313;border-radius:99px;height:5px;overflow:hidden;}
        .hp-pf{height:100%;background:linear-gradient(90deg,#c5a059,#e9c176);border-radius:99px;transition:width .4s;}
        .hp-modal-overlay{position:fixed;inset:0;background:rgba(0,0,0,.72);z-index:200;display:flex;align-items:center;justify-content:center;padding:20px;}
        .hp-modal{background:#1c1b1b;border:1px solid rgba(120,109,89,.65);border-radius:8px;padding:30px 28px;max-width:460px;width:100%;box-shadow:0 20px 60px rgba(0,0,0,.45);max-height:90vh;overflow-y:auto;}
        .hp-modal input,.hp-modal select{font-family:'Crimson Pro',Georgia,serif;font-size:1rem;background:#20201f;border:1px solid rgba(120,109,89,.85);border-radius:3px;padding:9px 13px;color:#f0e9dd;outline:none;width:100%;transition:border-color .2s;}
        .hp-modal input:focus,.hp-modal select:focus{border-color:#e2bf81;box-shadow:0 0 0 3px rgba(226,191,129,.2);}
        .hp-modal label{font-size:.72rem;font-weight:700;color:#d1c5b4;text-transform:uppercase;letter-spacing:.8px;display:block;margin-bottom:5px;}
        .hp-fgroup{display:flex;flex-direction:column;gap:5px;margin-bottom:14px;}
        .hp-modal-title{font-family:'Playfair Display',serif;font-size:1.3rem;font-weight:700;color:#f3eee4;margin-bottom:20px;}
        .hp-modal-footer{display:flex;gap:10px;justify-content:flex-end;margin-top:20px;padding-top:16px;border-top:1px solid rgba(120,109,89,.5);}
        @media(max-width:980px){
          .hp-shell{display:block;}
          .hp-sidebar{width:100%;border-right:none;border-bottom:1px solid rgba(53,53,53,.25);}
          .hp-main{padding:30px 16px 50px;}
        }
      `}</style>

      {/* HEADER */}
      <div className="hp-topbar">
        <div style={{ padding:"16px 28px", display:"flex", alignItems:"center", justifyContent:"space-between", gap:8 }}>
          <div style={{ display:"flex", alignItems:"center", gap:18 }}>
            <div>
              <span style={{ fontFamily:"'Playfair Display',serif", fontSize:"1.6rem", fontWeight:900, color:"#e5e2e1", fontStyle:"italic" }}>Autor</span>
              <span style={{ fontFamily:"'Playfair Display',serif", fontSize:"1.6rem", fontWeight:900, color:"#e9c176", fontStyle:"italic" }}>IA</span>
            </div>
            <div style={{height:16,width:1,background:"rgba(78,70,57,.5)"}}/>
            <span style={{fontSize:".75rem",letterSpacing:"0.18em",textTransform:"uppercase",color:"rgba(229,226,225,.78)"}}>Projetos</span>
          </div>
          <div style={{ display:"flex", alignItems:"center", gap:8 }}>
            <div style={{ width:8, height:8, borderRadius:"50%", background:"#4caf50", flexShrink:0 }}/>
            <span style={{ fontSize:".72rem", color:"rgba(229,226,225,.82)", maxWidth:160, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{user?.email}</span>
            <button onClick={onSignOut} style={{ background:"none", border:"1px solid rgba(78,70,57,.8)", color:"#d1c5b4", borderRadius:99, padding:"4px 10px", cursor:"pointer", fontSize:".72rem", fontFamily:"'Crimson Pro',Georgia,serif" }}>Sair</button>
          </div>
        </div>
      </div>

      <div className="hp-shell">
        {/* SIDE */}
        <aside className="hp-sidebar">
          <div style={{padding:"10px 12px 16px"}}>
            <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:16}}>
              <div style={{width:34,height:34,borderRadius:8,display:"flex",alignItems:"center",justifyContent:"center",background:"#1c1b1b"}}>
                <span className="material-symbols-outlined" style={{color:"#e9c176",fontSize:20}}>book_2</span>
              </div>
              <div>
                <div style={{fontFamily:"'Playfair Display',serif",fontSize:".95rem",color:"#e9c176"}}>The Digital Atelier</div>
                <div style={{fontSize:".62rem",letterSpacing:".18em",textTransform:"uppercase",color:"rgba(229,226,225,.6)"}}>Workspace</div>
              </div>
            </div>
            <button
              onClick={openNew}
              style={{width:"100%",background:"#c5a059",color:"#261900",border:"none",borderRadius:8,padding:"10px 12px",fontWeight:700,cursor:"pointer",fontFamily:"'Crimson Pro',Georgia,serif",display:"flex",justifyContent:"center",alignItems:"center",gap:6}}
            >
              <span className="material-symbols-outlined" style={{fontSize:18}}>add_circle</span>
              Novo Projeto
            </button>
          </div>
          <nav style={{padding:"6px 8px",display:"flex",flexDirection:"column",gap:4}}>
            <div style={{display:"flex",alignItems:"center",gap:10,padding:"10px 12px",borderRadius:8,background:"rgba(53,53,53,.3)",color:"#e9c176"}}>
              <span className="material-symbols-outlined" style={{fontSize:18}}>menu_book</span>
              <span style={{fontSize:".84rem"}}>Meus Livros</span>
            </div>
            <div style={{display:"flex",alignItems:"center",gap:10,padding:"10px 12px",borderRadius:8,color:"rgba(229,226,225,.65)"}}>
              <span className="material-symbols-outlined" style={{fontSize:18}}>settings</span>
              <span style={{fontSize:".84rem"}}>Configurações</span>
            </div>
          </nav>
        </aside>

        {/* MAIN */}
        <main className="hp-main">
          <div style={{ display:"flex", alignItems:"flex-end", justifyContent:"space-between", marginBottom:26, flexWrap:"wrap", gap:10 }}>
            <div>
              <h1 style={{ fontFamily:"'Playfair Display',serif", fontSize:"2.45rem", fontWeight:900, color:"#e9c176", lineHeight:1.1 }}>Meus Livros</h1>
              <p style={{ fontSize:".85rem", color:"rgba(229,226,225,.78)", marginTop:6 }}>
                {sorted.length === 0 ? "Nenhum projeto ainda" : `${sorted.length} projeto${sorted.length !== 1 ? "s" : ""} em andamento`}
              </p>
            </div>
            <button
              onClick={openNew}
              style={{background:"transparent",border:"1px solid rgba(197,160,89,.35)",color:"#e9c176",borderRadius:8,padding:"9px 12px",cursor:"pointer",fontFamily:"'Crimson Pro',Georgia,serif"}}
            >
              + Novo projeto
            </button>
          </div>

          <div className="hp-grid">
            {/* New project card */}
            <div className="hp-card-new" onClick={openNew} role="button" tabIndex={0} onKeyDown={e => e.key==="Enter" && openNew()}>
              <div style={{ fontSize:"3rem", color:"#e9c176", lineHeight:1 }}>+</div>
              <div style={{ fontFamily:"'Playfair Display',serif", fontSize:"1.1rem", fontWeight:700, color:"#e5e2e1" }}>Novo projeto</div>
              <div style={{ fontSize:".78rem", color:"rgba(229,226,225,.72)", textAlign:"center", maxWidth:190 }}>
                Comece uma nova jornada literária
              </div>
            </div>

            {sorted.map(project => {
              const { bibliaProgress, chapters, numChapters, phase, numBooks } = getProgress(project);
              const phaseColors = getPhaseColor(phase);
              const ago = timeAgo(project.updatedAt);
              const coverUrl = (project.data?.config?.coverUrl || "").trim();
              const genreShort = shortGenreLabel(project.genre);
              return (
                <div key={project.id} className="hp-card" onClick={() => onOpenProject(project)} role="button" tabIndex={0} onKeyDown={e => e.key==="Enter" && onOpenProject(project)}>
                  <div style={{
                    position:"relative",
                    aspectRatio:"3 / 4",
                    background:"#1a1a1a",
                    overflow:"hidden"
                  }}>
                    {coverUrl ? (
                      <div style={{
                        position:"absolute",
                        inset:0,
                        background:`center / contain no-repeat url("${coverUrl.replace(/"/g, '\\"')}")`
                      }}/>
                    ) : (
                      <div style={{
                        position:"absolute",
                        inset:0,
                        background:"linear-gradient(145deg, #272727, #1a1a1a)"
                      }}/>
                    )}
                    <div style={{position:"absolute",left:0,right:0,bottom:0,height:"38%",background:"linear-gradient(to top, rgba(20,20,20,.94), rgba(20,20,20,.35), transparent)"}}/>
                  </div>
                  <div style={{padding:"20px 18px",display:"flex",flexDirection:"column",gap:12}}>
                    <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",gap:10}}>
                      <div>
                        <div style={{ fontFamily:"'Playfair Display',serif", fontSize:"1.38rem", fontWeight:700, color:"#e5e2e1", lineHeight:1.2 }}>
                          {project.title || "Sem título"}
                        </div>
                        <div style={{display:"flex",alignItems:"center",gap:8,marginTop:4}}>
                          <span style={{fontSize:".68rem",color:"rgba(229,226,225,.72)",textTransform:"uppercase",letterSpacing:".1em"}}>{getPhaseLabel(phase)}</span>
                          {genreShort && (
                            <span
                              title={project.genre || genreShort}
                              style={{fontSize:".62rem",padding:"1px 7px",borderRadius:999,background:"rgba(14,14,14,.55)",border:"1px solid rgba(197,160,89,.25)",color:"#e9c176",letterSpacing:".05em",textTransform:"uppercase"}}
                            >
                              {genreShort}
                            </span>
                          )}
                          {numBooks > 1 && <span style={{ fontSize:".64rem", background:"rgba(233,193,118,.12)", color:"#e9c176", padding:"1px 7px", borderRadius:99, fontWeight:600 }}>{numBooks} livros</span>}
                        </div>
                      </div>
                      <button className="hp-edit-btn" onClick={e => openEdit(e, project)}>⚙ Config</button>
                    </div>

                    <div>
                      <div style={{ display:"flex", justifyContent:"space-between", fontSize:".67rem", color:"rgba(229,226,225,.78)", marginBottom:4 }}>
                        <span>Bíblia</span><span>{Math.min(bibliaProgress,100)}%</span>
                      </div>
                      <div className="hp-pw"><div className="hp-pf" style={{ width:`${Math.min(bibliaProgress,100)}%` }}/></div>
                    </div>

                    {numChapters > 0 && (
                      <div style={{ fontSize:".78rem", color:"#d1c5b4" }}>✍ {chapters}/{numChapters} capítulo{numChapters!==1?"s":""}</div>
                    )}

                    <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginTop:2}}>
                      <span style={{display:"inline-flex",alignItems:"center",padding:"2px 9px",borderRadius:99,fontSize:".68rem",fontWeight:700,background:phaseColors.bg,color:phaseColors.color}}>
                        {getPhaseLabel(phase)}
                      </span>
                      {ago && <span style={{ fontSize:".68rem", color:"rgba(229,226,225,.7)" }}>{ago}</span>}
                    </div>
                    <button className="hp-continue-btn" onClick={e => { e.stopPropagation(); onOpenProject(project); }}>
                      Continuar Escrevendo
                      <span className="material-symbols-outlined" style={{fontSize:18}}>auto_stories</span>
                    </button>
                  </div>
                </div>
              );
            })}
          </div>

          <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(220px,1fr))",gap:14,marginTop:26,opacity:.85}}>
            <div style={{background:"#1c1b1b",padding:"16px 14px",borderRadius:10,borderLeft:"2px solid #c5a059"}}>
              <div style={{fontSize:".62rem",letterSpacing:".18em",textTransform:"uppercase",color:"#e9c176"}}>Total de palavras</div>
              <div style={{fontFamily:"'Playfair Display',serif",fontSize:"1.8rem",marginTop:6,color:"#e5e2e1"}}>{totalWords.toLocaleString("pt-BR")}</div>
            </div>
            <div style={{background:"#1c1b1b",padding:"16px 14px",borderRadius:10,borderLeft:"2px solid #c5a059"}}>
              <div style={{fontSize:".62rem",letterSpacing:".18em",textTransform:"uppercase",color:"#e9c176"}}>Projetos ativos</div>
              <div style={{fontFamily:"'Playfair Display',serif",fontSize:"1.8rem",marginTop:6,color:"#e5e2e1"}}>{sorted.length}</div>
            </div>
          </div>
        </main>
      </div>

      {/* MODAL — Novo projeto / Configurar */}
      {modal && (
        <div className="hp-modal-overlay" onClick={() => setModal(null)}>
          <div className="hp-modal" onClick={e => e.stopPropagation()}>
            <div className="hp-modal-title">
              {modal.mode === "new" ? "✦ Novo Projeto" : "⚙️ Configurar Projeto"}
            </div>

            <div className="hp-fgroup">
              <label>Título do livro {form.numBooks > 1 ? "(nome da série)" : ""}</label>
              <input autoFocus value={form.title} onChange={e => setForm(f => ({...f, title: e.target.value}))} placeholder="Ex: O Acorde de Sangue" onKeyDown={e => e.key==="Enter" && handleSubmit()}/>
            </div>

            <div className="hp-fgroup">
              <label>Nome do autor</label>
              <input value={form.author} onChange={e => setForm(f => ({...f, author: e.target.value}))} placeholder="Seu nome ou pseudônimo"/>
            </div>

            <div className="hp-fgroup">
              <label>Capa do livro (opcional)</label>
              <input
                ref={coverInputRef}
                type="file"
                accept="image/*"
                onChange={handleCoverFileChange}
                style={{ display:"none" }}
              />
              <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
                <button
                  type="button"
                  onClick={handlePickCover}
                  style={{ background:"#2a2a2a", border:"1px solid rgba(120,109,89,.75)", color:"#ede2d1", borderRadius:3, padding:"8px 12px", cursor:"pointer", fontFamily:"'Crimson Pro',Georgia,serif", fontSize:".9rem" }}
                >
                  📁 Escolher do computador
                </button>
                {form.coverUrl && (
                  <button
                    type="button"
                    onClick={() => setForm(f => ({...f, coverUrl: ""}))}
                    style={{ background:"none", border:"1px solid rgba(120,109,89,.75)", color:"#d1c5b4", borderRadius:3, padding:"8px 12px", cursor:"pointer", fontFamily:"'Crimson Pro',Georgia,serif", fontSize:".9rem" }}
                  >
                    Remover capa
                  </button>
                )}
              </div>
              {form.coverUrl && (
                <div style={{marginTop:8,border:"1px solid rgba(120,109,89,.55)",borderRadius:4,overflow:"hidden"}}>
                  <img src={form.coverUrl} alt="Prévia da capa" style={{display:"block",width:"100%",maxHeight:180,objectFit:"cover"}} />
                </div>
              )}
            </div>

            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12, marginBottom:14 }}>
              <div className="hp-fgroup" style={{ marginBottom:0 }}>
                <label>Nº de livros</label>
                <select value={form.numBooks} onChange={e => setNumBooks(e.target.value)}>
                  {Array.from({length:10},(_,i)=><option key={i+1} value={i+1}>{i+1} livro{i>0?"s":""}</option>)}
                </select>
              </div>
              <div className="hp-fgroup" style={{ marginBottom:0 }}>
                <label>Capítulos por livro</label>
                <select value={form.numChapters} onChange={e => setForm(f => ({...f, numChapters: Number(e.target.value)}))}>
                  {[5,6,8,10,12,15,20,25,30,40,50].map(n => <option key={n} value={n}>{n} capítulos</option>)}
                </select>
              </div>
            </div>

            {/* Chronicle / Universe name — only for series */}
            {form.numBooks > 1 && (
              <div style={{ background:"#20201f", border:"1px solid rgba(120,109,89,.55)", borderRadius:4, padding:"12px 14px", marginBottom:14 }}>
                {/* Chronicle name */}
                <div style={{ marginBottom:12 }}>
                  <label style={{ display:"flex", alignItems:"center", gap:6, marginBottom:6 }}>
                    <span>🌌 Nome das Crônicas / Universo</span>
                    <span style={{ fontSize:".68rem", color:"#d1c5b4", fontWeight:400 }}>(opcional)</span>
                  </label>
                  <input
                    value={form.chronicleName || ""}
                    onChange={e => setForm(f => ({...f, chronicleName: e.target.value}))}
                    placeholder="Ex: Crônicas de Lúmina, O Ciclo de Duna, O Mundo de Nárnia..."
                    style={{ marginBottom:0 }}
                  />
                  <div style={{ fontSize:".68rem", color:"#d1c5b4", marginTop:4 }}>
                    O guarda-chuva que engloba toda a série. Aparece no topo da Bíblia.
                  </div>
                </div>

                {/* Individual book titles */}
                <label style={{ marginBottom:8, display:"block", fontWeight:600 }}>Título de cada livro</label>
                <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
                  {Array.from({length: form.numBooks}, (_, i) => (
                    <div key={i} style={{ display:"flex", alignItems:"center", gap:8 }}>
                      <span style={{ fontSize:".72rem", color:"#d1c5b4", minWidth:48, fontWeight:600 }}>Livro {i+1}</span>
                      <input
                        value={form.bookTitles[i] || ""}
                        onChange={e => setBookTitle(i, e.target.value)}
                        placeholder={`Título do Livro ${i+1}`}
                        style={{ marginBottom:0 }}
                      />
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="hp-modal-footer">
              <button onClick={() => setModal(null)} style={{ background:"#2a2a2a", border:"1px solid rgba(120,109,89,.75)", color:"#ede2d1", borderRadius:3, padding:"9px 18px", cursor:"pointer", fontFamily:"'Crimson Pro',Georgia,serif", fontSize:".95rem" }}>
                Cancelar
              </button>
              <button
                onClick={handleSubmit}
                disabled={!form.title.trim()}
                style={{ background:"linear-gradient(135deg,#b8892a,#d4a94a)", color:"#1a1208", border:"none", borderRadius:3, padding:"9px 22px", cursor:"pointer", fontFamily:"'Crimson Pro',Georgia,serif", fontSize:".95rem", fontWeight:700, opacity: form.title.trim() ? 1 : 0.5 }}
              >
                {modal.mode === "new" ? "Criar projeto →" : "Salvar"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
